import { useDebounce } from 'ahooks';
import { Form } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage, AppModal } from '@/common/components';
import { AppModalControlProps, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
  PageSearchAndReplaceFormFields,
  PageSearchAndReplaceFormInputs,
  PagesQueryKeys,
  useAutocompleteSearchAndReplaceQuery,
  useSearchAndReplaceTextPagesMutation,
} from '@/modules/Pages';

import { PagesSearchAndReplaceFormView } from './PagesSearchAndReplaceFormView';
import { pageSearchAndReplaceFormValidationSchema } from './validation';

interface Props {
  siteId: string;
  controls: AppModalControlProps;
}

export const PagesSearchAndReplaceFormContainer: FC<Props> = ({ siteId, controls }) => {
  const appMessageApi = AppMessage.useControls();
  const queryClient = useQueryClient();

  const [form] = Form.useForm<PageSearchAndReplaceFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof PageSearchAndReplaceFormInputs>>(null);

  const watchedAutocompleteFields = Form.useWatch((values) => {
    const { replaceWith, ...rest } = values;
    return rest;
  }, form);

  const debouncedValues = useDebounce(watchedAutocompleteFields, { wait: 550 });

  useEffect(() => {
    setFormErrors(null);
  }, [watchedAutocompleteFields]);

  const {
    isLoading: autocompleteIsLoading,
    isFetching: autocompleteIsFetching,
    error: autocompleteError,
    isError: autocompleteIsError,
    data: autocompleteData,
  } = useAutocompleteSearchAndReplaceQuery({ query: debouncedValues }, siteId)({ enabled: !!debouncedValues?.searchFor });

  const { mutate: searchAndReplaceTextPagesMutation, isLoading: searchAndReplaceTextPagesIsLoading } =
    useSearchAndReplaceTextPagesMutation(siteId)({
      onSuccess: async () => {
        appMessageApi.success('Запит на пошук та заміну відправлено успішно!');
        await queryClient.invalidateQueries([PagesQueryKeys.GetAll], { exact: false });
        await queryClient.invalidateQueries([PagesQueryKeys.SearchAndReplaceHistory, siteId]);
        handleModalClose();
      },
      onError: (error) => {
        setFormErrors(extractFormErrors<typeof PageSearchAndReplaceFormInputs>(error));
        messageOnError(error, appMessageApi);
      },
    });

  const initialValues: PageSearchAndReplaceFormFields = {
    searchFor: undefined,
    replaceWith: '',
    matchCase: false,
    wholeWord: false,
    useRegex: false,
  };

  const handleFinish = useCallback((values: PageSearchAndReplaceFormFields): void => {
    setFormErrors(null);
    searchAndReplaceTextPagesMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleModalClose = useCallback((): void => {
    setFormErrors(null);
    form.resetFields();
    controls.close();
  }, [controls, form]);

  return (
    <AppModal title="Знайти та замінити" width={500} footer={false} closable={!autocompleteIsFetching} controls={controls} afterClose={
      handleModalClose
    }>
      <PagesSearchAndReplaceFormView
        formName="pages-search-and-replace-form"
        formType="edit"
        form={form}
        formErrors={formErrors}
        initialValues={initialValues}
        isFormLoading={searchAndReplaceTextPagesIsLoading}
        validationSchema={pageSearchAndReplaceFormValidationSchema}
        autocompleteData={autocompleteData}
        autocompleteIsLoading={autocompleteIsLoading}
        autocompleteIsFetching={autocompleteIsFetching}
        autocompleteIsError={autocompleteIsError}
        autocompleteError={autocompleteError}
        handleBack={handleModalClose}
        handleFinish={handleFinish}
        handleFinishFailed={handleFinishFailed}
      />
    </AppModal>
  );
};
